import { apiTransformer, useErrors, validationTransformer, type ErrorTransformer } from '../utils';

export const billingApiTransformer = () =>
  apiTransformer((data, error) => {
    // Server-side failures rarely carry a message that's safe or useful to show
    // a user, so collapse every 5xx to a single, neutral, retry-able message.
    if (data.status >= 500) {
      return {
        message: 'Something went wrong on our end. Please try again in a moment.',
        code: data.code,
        level: 'danger',
        error,
      };
    }

    switch (data.status) {
      case 400:
        return {
          message: data.message,
          code: data.code,
          level: 'danger',
          error,
        };
      case 402:
        if (data.code === 'ACCOUNT_RESTRICTED') {
          return {
            message: data.message,
            code: data.code,
            level: 'danger',
            error,
            actions: [{ text: 'View', as: 'primary', to: { name: 'billing.index' } }],
          };
        }

        break;
    }

    switch (data.code) {
      case 'COMPANY_NOT_FOUND':
        return {
          message: 'Company setup required.',
          code: data.code,
          level: 'danger',
          error,
          actions: [{ text: 'Fix', as: 'primary', to: { name: 'projects.settings' } }],
        };
      case 'SETUP_INTENT_FAILED':
        return {
          message: 'Failed to create a payment method.',
          code: data.code,
          level: 'danger',
          error,
        };
      case 'INSUFFICIENT_BALANCE':
        return {
          message: 'Balance is insufficient to complete this charge.',
          code: data.code,
          level: 'danger',
          error,
        };
    }
  });

export const billingFlagTransformer = (): ErrorTransformer<unknown> => (flag) => {
  switch (flag) {
    case 'payment_method_required':
      return {
        message: 'Missing payment method.',
        level: 'danger',
        code: 'PAYMENT_METHOD_REQUIRED',
        actions: [
          {
            text: 'Add',
            as: 'primary',
            to: { name: 'billing.payment-methods' },
          },
        ],
      };
    case 'payment_method_expiring':
      return {
        message: 'Payment method expiring soon.',
        level: 'warning',
        code: 'PAYMENT_METHOD_EXPIRING',
        actions: [{ text: 'Update', as: 'primary', to: { name: 'billing.payment-methods' } }],
      };
    case 'payment_method_expired':
      return {
        message: 'Payment method expired.',
        level: 'danger',
        code: 'PAYMENT_METHOD_EXPIRED',
        actions: [{ text: 'Fix', as: 'primary', to: { name: 'billing.payment-methods' } }],
      };
    case 'credit_limit_warning':
      return {
        message: 'Near credit limit.',
        code: 'CREDIT_LIMIT_WARNING',
        level: 'warning',
        actions: [{ text: 'View', as: 'primary', to: { name: 'billing.index' } }],
      };
    case 'credit_limit_exceeded':
      return {
        message: 'Credit limit exceeded.',
        code: 'CREDIT_LIMIT_EXCEEDED',
        level: 'danger',
        actions: [{ text: 'View', as: 'primary', to: { name: 'billing.index' } }],
      };
    case 'has_overdue_invoices':
      return {
        message: 'Account has overdue invoices.',
        code: 'HAS_OVERDUE_INVOICES',
        level: 'danger',
        actions: [{ text: 'View', as: 'primary', to: { name: 'billing.invoices', query: { 'search-invoices': 'status:overdue' } } }],
      };
    case 'credit_control':
      return {
        message: 'Account is under credit control.',
        code: 'CREDIT_CONTROL',
        level: 'danger',
        actions: [{ text: 'View', as: 'primary', to: { name: 'billing.index' } }],
      };
    case 'account_restricted':
      return {
        message: 'Account is restricted until billing issues are resolved.',
        code: 'ACCOUNT_RESTRICTED',
        level: 'danger',
        actions: [{ text: 'View', as: 'primary', to: { name: 'billing.index' } }],
      };
    default:
      return;
  }
};

export const useBillingErrors = () =>
  useErrors([billingApiTransformer(), validationTransformer, billingFlagTransformer()]);
